import {
  CheckCircleOutlined as CheckCircleOutlinedIcon,
  DoNotDisturb as DoNotDisturbIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  List,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { colors } from '../../constants/constants';
import { ItemTypes } from '../../redux/reducers/assessmentReducer';
import { selectTitle } from '../../redux/reducers/catalogsReducer';
import { selectNameList } from '../../redux/reducers/organizationReducer';
import SOAEditControl from './SOAEditControl';
import {
  TypeCategories,
  selectCatalogGroups,
  selectSoaControls,
  selectAreAllControlsComplete,
} from '../../redux/reducers/frameworkReducer';

function DocLink({ document }) {
  return (
    <a
      href={
        document.link.startsWith('https://') || document.link.startsWith('http://')
          ? document.link
          : `//${document.link}`
      }
      target="_blank"
      rel="noopener noreferrer"
    >
      <Typography variant="documentLink" color="primary">
        {document.documentName}
      </Typography>
    </a>
  );
}

function ExpandedControl({ control }) {
  const { t } = useTranslation();
  const ownerName = useSelector((state) => selectNameList(state, control.owner));

  const getJustificationText = () => {
    if (!control.applicable) {
      return t('control.exclusionJustification');
    }
    return t('control.applicabilityJustification');
  };

  const getChipLabel = (reason, applicable) => {
    let label;
    if (applicable) {
      label = `${t(`control.evaluation.applicability.${reason}Desc`)} (${t(`control.evaluation.applicability.${reason}`)})`;
    } else {
      label = `${t(`control.evaluation.inApplicability.${reason}Desc`)}`;
    }
    return label;
  };

  return (
    <CardContent variant="controlDetailsContent">
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Stack>
            <Typography>{getJustificationText()}</Typography>
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {
              control.applicabilityReasons.map((reason) => (
                <Chip
                  variant="controlChip"
                  label={getChipLabel(reason, control.applicable)}
                  color={control.applicable ? 'primary' : 'secondary'}
                />

              ))
            }
          </Stack>
          <Typography variant="body2">
            {(control.applicable
              ? control.description
              : control.exclusionJustification) || '-'}
          </Typography>
        </Stack>
        {control.applicable && (
          <Stack>
            <Typography variant="body1">{t('control.owner')}</Typography>
            <Typography variant="body2">{(ownerName.length > 0 ? ownerName : '-')}</Typography>
          </Stack>
        )}
        <Stack spacing={1} sx={{ pb: '5px' }}>
          <Typography variant="body1">{t('control.evaluation.evidenceLink')}</Typography>
          {control.links.length > 0 ? (
            control.links.map((link, index) => (
              <DocLink
                key={index}
                document={link}
              />
            ))
          ) : (
            <Typography>-</Typography>
          )}
        </Stack>
      </Stack>
    </CardContent>
  );
}

function ControlRow({ subControl, complete, margin }) {
  const { t } = useTranslation();
  const title = useSelector((state) => selectTitle(state, subControl.id));
  const [expandControl, setExpandControl] = useState(false);

  const toggleShowControl = () => {
    setExpandControl(!expandControl);
  };

  const [editModalOpen, setEditModalOpen] = useState(false);

  const handleControlEdeting = () => {
    setEditModalOpen(true);
  };

  const handleCancel = () => {
    setEditModalOpen(false);
  };

  return (
    <>
      {editModalOpen && (<SOAEditControl control={subControl} handleClose={handleCancel} />)}
      <Card sx={{
        px: '10px',
        py: '3px',
        mb: margin,
        boxShadow: 3,
        backgroundColor: !subControl.applicable && subControl.finished ? colors.notApplicable : colors.white,
      }}
      >
        <CardContent variant="controlDetailsContent">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="controlTitle">
              {title}
            </Typography>
            <Stack direction="row" alignItems="center">
              {
                !expandControl && subControl.applicable
                && (
                  <Stack direction="row" spacing={1} justifyContent="flex-end" useFlexGap flexWrap="wrap">
                    {subControl.applicabilityReasons.map((reason) => (
                      <Chip
                        key={reason}
                        variant="controlChip"
                        label={reason.toUpperCase()}
                        color="primary"
                      />

                    ))}
                  </Stack>
                )
              }

              {complete ? (
                <Stack variant="applicableTitle">
                  {
                    subControl.applicable
                      ? <CheckCircleOutlinedIcon color="primary" sx={{ m: '8px' }} />
                      : <DoNotDisturbIcon color="error" sx={{ m: '8px' }} />
                  }
                  <Typography variant="soaApplicability">
                    {subControl.applicable ? t('control.applicable') : t('control.notApplicable')}
                  </Typography>
                </Stack>
              ) : (
                <Stack variant="incompleteChip">

                  <Chip variant="controlChip" label={t('control.incomplete')} color="warning" />
                </Stack>

              )}
              <Stack variant="controlIcons">
                <IconButton id="edit-control" onClick={handleControlEdeting}>
                  <EditIcon />
                </IconButton>
                <IconButton id="expand-or-collapse-control" onClick={toggleShowControl}>
                  {expandControl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
        <Collapse in={expandControl} unmountOnExit>
          <ExpandedControl control={subControl} />
        </Collapse>
      </Card>
    </>
  );
}

function GroupedSubControls({ group }) {
  const controls = (id) => {
    return useSelector((state) => selectSoaControls(state, id));
  };
  const [showControls, setShowControls] = useState(true);

  const title = (id) => {
    return useSelector((state) => selectTitle(state, id));
  };

  const toggleShowControls = () => {
    setShowControls(!showControls);
  };

  return (
    <Card variant="controlDetails">
      <CardContent variant="controlDetailsContent">
        <Stack direction="row" alignItems="center">
          <Typography>{title(group.id)}</Typography>
          <IconButton id="expand-or-collapse-title" onClick={toggleShowControls}>
            {showControls ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
      </CardContent>
      <Collapse in={showControls} unmountOnExit>
        <CardContent sx={{ px: '15px' }} variant="controlDetailsContent">
          <Stack spacing={1} sx={{ pb: 1 }}>
            {controls(group.id).map((control) => (
              control.type === ItemTypes.GROUP ? (
                <Stack key={`${control.id}-row`}>
                  <Typography>{title(control.id)}</Typography>
                  {
                    controls(control.id).map((subControl) => (
                      <ControlRow key={`${subControl.id}-row`} subControl={subControl} complete={subControl.finished} margin="5px" />

                    ))
                  }
                </Stack>
              ) : (
                <ControlRow key={`${control.id}-row`} subControl={control} complete={control.finished} />
              )
            ))}

          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
}

function SOAControlList() {
  const { t } = useTranslation();
  const catalogGroups = useSelector((state) => selectCatalogGroups(state, TypeCategories.OPERATIONAL));

  const controls = (id) => {
    return useSelector((state) => selectSoaControls(state, id));
  };
  const allComplete = useSelector(selectAreAllControlsComplete);

  return (
    <Box>
      <Stack spacing={1}>
        <Typography variant="soaSubTitle">{t('framework.soa.listOfControls')}</Typography>
        {!allComplete
          && (
            <Stack spacing={2}>
              <Typography variant="soaIncompleteTitle" color="warning.main">
                {t('framework.soa.containsIncompleteControls')}
              </Typography>
              <Typography variant="viewDesc">{t('framework.soa.incompleteDescription')}</Typography>
            </Stack>
          )}
      </Stack>
      {catalogGroups.map((catalog) => (
        <List key={catalog.id} spacing={100}>
          {controls(catalog.id).map((group) => (
            <GroupedSubControls key={`${group.id}-row`} group={group} />
          ))}
        </List>
      ))}
    </Box>
  );
}

export default SOAControlList;
